/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';
import { FloatingPortal, autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/react';

import type { DatePickerProps } from '../../types';
import Icon from '../Icon/Icon';
import IconButton from '../IconButton/IconButton';
import './DatePicker.less';

const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const defaultFormat = (d: Date) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

// Full 6-week (42-cell) grid so the calendar height stays stable across months.
function buildMonthGrid(view: Date): Date[] {
  const first = startOfMonth(view);
  const gridStart = new Date(first);
  gridStart.setDate(first.getDate() - first.getDay());
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });
}

/**
 * DatePicker — a calendar date field. Works with native `Date` values so the
 * DS has no date-library dependency; consumers using moment/dayjs convert at
 * the boundary (`value={m.toDate()}`, `onChange={d => setM(d && moment(d))}`).
 */
const DatePicker = React.forwardRef<HTMLButtonElement, DatePickerProps>(
  (
    {
      value,
      onChange,
      disabledDate,
      formatDate = defaultFormat,
      placeholder = 'Select date',
      disabled = false,
      allowClear = true,
      size = 'medium',
      id,
      name,
      className,
      'aria-label': ariaLabel,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);
    const [viewDate, setViewDate] = React.useState<Date>(() => startOfMonth(value ?? new Date()));
    const triggerRef = React.useRef<HTMLButtonElement | null>(null);

    const { refs, floatingStyles } = useFloating({
      placement: 'bottom-start',
      strategy: 'fixed',
      middleware: [offset(4), flip(), shift({ padding: 8 })],
      whileElementsMounted: autoUpdate,
    });

    const setTriggerRef = React.useCallback(
      (node: HTMLButtonElement | null) => {
        triggerRef.current = node;
        refs.setReference(node);
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
      },
      [ref, refs]
    );

    // Keep the visible month in sync when the value changes.
    React.useEffect(() => {
      if (value) setViewDate(startOfMonth(value));
    }, [value]);

    const close = React.useCallback(() => setOpen(false), []);

    React.useEffect(() => {
      if (!open) return undefined;
      const onDown = (e: MouseEvent) => {
        const t = e.target as Node;
        if (
          triggerRef.current &&
          !triggerRef.current.contains(t) &&
          refs.floating.current &&
          !refs.floating.current.contains(t)
        ) {
          close();
        }
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === 'Escape') close();
      };
      document.addEventListener('mousedown', onDown);
      document.addEventListener('keydown', onKey);
      return () => {
        document.removeEventListener('mousedown', onDown);
        document.removeEventListener('keydown', onKey);
      };
    }, [open, close, refs.floating]);

    const grid = React.useMemo(() => buildMonthGrid(viewDate), [viewDate]);
    const monthLabel = viewDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const showClear = allowClear && !!value && !disabled;

    return (
      <span
        className={cx(
          'ds-date-picker',
          `ds-date-picker--${size}`,
          { 'ds-date-picker--disabled': disabled },
          className
        )}>
        <button
          ref={setTriggerRef}
          type="button"
          id={id}
          name={name}
          className="ds-date-picker__trigger"
          disabled={disabled}
          aria-label={ariaLabel ?? (value ? formatDate(value) : placeholder)}
          aria-haspopup="dialog"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}>
          <span className={cx('ds-date-picker__value', { 'ds-date-picker__value--placeholder': !value })}>
            {value ? formatDate(value) : placeholder}
          </span>
          <Icon name="field-type/date" size="small" className="ds-date-picker__icon" />
        </button>

        {showClear && (
          <button
            type="button"
            aria-label="Clear date"
            className="ds-date-picker__clear"
            onClick={() => onChange?.(null)}>
            <Icon name="actionable/clear" size="small" />
          </button>
        )}

        {open && (
          <FloatingPortal>
            <div ref={refs.setFloating} className="ds-date-picker__popover" style={floatingStyles} role="dialog">
              <div className="ds-date-picker__header">
                <IconButton
                  icon="actionable/chevron-left"
                  aria-label="Previous month"
                  variant="tertiary"
                  size="small"
                  onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
                />
                <span className="ds-date-picker__month-label">{monthLabel}</span>
                <IconButton
                  icon="actionable/chevron-right"
                  aria-label="Next month"
                  variant="tertiary"
                  size="small"
                  onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
                />
              </div>
              <div className="ds-date-picker__weekdays">
                {WEEKDAYS.map((w) => (
                  <span key={w} className="ds-date-picker__weekday">
                    {w}
                  </span>
                ))}
              </div>
              <div className="ds-date-picker__grid">
                {grid.map((d) => {
                  const outside = d.getMonth() !== viewDate.getMonth();
                  const isDisabled = disabledDate?.(d) ?? false;
                  const selected = value ? isSameDay(d, value) : false;
                  return (
                    <button
                      key={d.toISOString()}
                      type="button"
                      className={cx('ds-date-picker__day', {
                        'ds-date-picker__day--outside': outside,
                        'ds-date-picker__day--selected': selected,
                      })}
                      disabled={isDisabled}
                      aria-pressed={selected}
                      onClick={() => {
                        onChange?.(d);
                        close();
                      }}>
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
          </FloatingPortal>
        )}
      </span>
    );
  }
);

DatePicker.displayName = 'DatePicker';

export default DatePicker;
